/**
 * Gets the repositories of the user from Github
 */
import IntlMessageFormat from 'intl-messageformat';
import * as Raven from 'raven-js';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import request from 'utils/request';
import { loginSuccess, setSelectedPolicyId, setFnolReportDateValidation, setSelectedClientId, setEmailAddressLogin, setPhoneNumberLogin, setIsPolicyCancelled } from 'containers/App/actions';
import { setError, clearError } from 'containers/App/actions';
import apiConfig from 'utils/apiConfig';
import { startProgress, endProgress } from 'containers/ProgressBar/actions';
import { RAVEN_LOG_LEVEL } from 'utils/constants';
import { SCTI_COUNTRY_IDS, CELLCAPTIVE_NAME } from 'containers/FastCover/constants';
import { makeSelectLocale } from 'containers/LanguageProvider/selectors';
import {
  makeSelectLoginInfo,
  makeSelectAuthToken,
  makeSelectVerificationCode,
} from './selectors';
import {
  setAuthenticationToken,
  setRequireVerificationCode,
  setUserName,
  loading,
  setPurchaseDate,
  setPolicyNumber,
} from './actions';
import { VALIDATE_VERIFICATION_CODE, VALIDATE_POLICY_INFO } from './constants';
import { translationMessages } from '../../i18n';
import { appConfig } from '../../configureApp';
import messages from './messages';

export const isFaqVisible = apiConfig.isFaqVisible;
let isActivePolicy = true;
let isDateValid = true;

const getLocaleMessages = (locale) =>
  (locale && translationMessages[locale]) || translationMessages.en || {};

const formatDescriptor = (descriptor, locale, values = {}) => {
  if (!descriptor) {
    return '';
  }
  const localeMessages = getLocaleMessages(locale);
  const message =
    (descriptor.id && localeMessages[descriptor.id]) || descriptor.defaultMessage || '';
  try {
    const formatter = new IntlMessageFormat(message, locale);
    return formatter.format(values);
  } catch (err) {
    return message;
  }
};

const getLoginFailDescriptor = (cellCaptiveCountryId) => {
  if (cellCaptiveCountryId === SCTI_COUNTRY_IDS.NEW_ZEALAND) {
    return messages.loginFailNz;
  }
  if (cellCaptiveCountryId === SCTI_COUNTRY_IDS.AUSTRALIA) {
    return messages.loginFailAu;
  }
  return messages.loginFail;
};

const buildPortalLink = (locale) => {
  const portalConfig = appConfig.customerPortal || {};
  const textSource = portalConfig && portalConfig.text;
  let portalText = '';
  if (typeof textSource === 'string') {
    portalText = textSource;
  } else if (textSource && typeof textSource === 'object') {
    portalText = textSource[locale] || textSource.en || textSource.default || '';
  }
  if (!portalText) {
    portalText = formatDescriptor(messages.portalLinkFallback, locale);
  }
  const href = portalConfig && portalConfig.href;
  if (href) {
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${portalText}</a>`;
  }
  return portalText;
};

/**
 * Github repos request/response handler
 */
function* getReportDateValidationStatus() {
  const token = yield select(makeSelectAuthToken());
  const requestUrl = `${apiConfig.restServiceDomain
    }/rest/validationSettings`;
  const requestOption = {
    method: 'GET',
    headers: {
      Authorization: `bearer ${token}`,
    },
  };

  return { requestUrl, requestOption };
}

export function* validateEmailPolicyNumber() {
  isActivePolicy = true;
  isDateValid = true;
  const authModel = yield select(makeSelectLoginInfo());
  const locale = yield select(makeSelectLocale());
  const portalLink = buildPortalLink(locale);
  const formatMessage = (descriptor, values = {}) =>
    formatDescriptor(descriptor, locale, values);
  // Verification Token
  const requestURL = `${apiConfig.restServiceDomain
    }/api/v1/auth/verificationToken`;
  const options = {
    method: 'POST',
    body: JSON.stringify(authModel),
    headers: {
      'Content-Type': 'application/json',
    },
  };
  try {
    yield put(clearError());
    yield put(startProgress());
    yield put(loading(true));
    const response = yield call(request, requestURL, options);
    const loginFailDescriptor = getLoginFailDescriptor(response.cellCaptiveCountryId);
    const localizedLoginFailMessage = formatMessage(loginFailDescriptor, { portalLink });
    const errorMessage = response.cellCaptiveName === CELLCAPTIVE_NAME.NEW_ZEALAND
      ? messages.loginFailNz.defaultMessage
      : response.cellCaptiveName === CELLCAPTIVE_NAME.AUSTRALIA
        ? messages.loginFailAu.defaultMessage
        :  response.cellCaptiveName === SCTI_COUNTRY_IDS.PARTNER_INSURANCE
          ? messages.loginFailPi.defaultMessage
          : messages.loginFail.defaultMessage;
    if (!!response.message) {
      if (response.message === 'emailTemplateNotConfigured') {
        yield put(setError(formatMessage(messages.loginSystemIssue)));
        return;
      }
      if (response.message === 'date') {
        isDateValid = false;
        yield put(setPurchaseDate(response.purchaseDate));
        yield put(setError(localizedLoginFailMessage));
      }
      if (response.message === 'quote') {
        isActivePolicy = false;
        yield put(setError(localizedLoginFailMessage));
      }
      throw new Error();
    }
    yield put(setAuthenticationToken(response.token));
    yield put(setRequireVerificationCode(true));
    Raven.setUserContext({
      email: authModel.email,
      policyNumber: authModel.policyNumber,
    });
  } catch (err) {
    const data = err && err.data ? err.data : {};
    Raven.captureException(err);

    let loginFailDescriptor = messages.loginFail;
    if (data && typeof data.cellCaptiveCountryId !== 'undefined') {
      loginFailDescriptor = getLoginFailDescriptor(data.cellCaptiveCountryId);
    }
    let errorMessage = formatMessage(loginFailDescriptor, { portalLink });
    if (data.cellCaptiveName === CELLCAPTIVE_NAME.NEW_ZEALAND) {
      errorMessage = messages.loginFailNz.defaultMessage;
    } else if (data.cellCaptiveName === CELLCAPTIVE_NAME.AUSTRALIA) {
      errorMessage = messages.loginFailAu.defaultMessage;
    } else if (data.cellCaptiveName === CELLCAPTIVE_NAME.PARTNER_INSURANCE) {
      errorMessage = messages.loginFailPi.defaultMessage;
    }

    if (isDateValid && isActivePolicy) {
      yield put(setError(errorMessage));
    }
    // yield put(showErrorToastr(messages.loginFail.defaultMessage));
  } finally {
    yield put(endProgress());
    yield put(loading(false));
  }
}

export function* validateVerificationCode() {
  const authModel = yield select(makeSelectLoginInfo());
  const token = yield select(makeSelectAuthToken());
  const verificationCode = yield select(makeSelectVerificationCode());
  const requestURL = `${apiConfig.restServiceDomain}/api/v1/auth/token`;
  const options = {
    method: 'POST',
    body: JSON.stringify(authModel),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `bearer ${token}`,
      'X-Verification-Code': verificationCode,
    },
  };
  const {
    requestUrl: reportDateValidateStatusRequest,
    requestOption: reportDateValidateStatusRequestOption,
  } = yield getReportDateValidationStatus();
  try {
    yield put(clearError());
    Raven.captureMessage('validateVerificationCode', RAVEN_LOG_LEVEL);
    yield put(startProgress());
    yield put(loading(true));
    const response = yield call(request, requestURL, options);
    yield put(setAuthenticationToken(response.token));
    yield put(setSelectedPolicyId(response.policyId));
    yield put(setSelectedClientId(response.clientId));
    yield put(setUserName(response.clientName));
    yield put(setPolicyNumber(authModel.policyNumber));
    yield put(setEmailAddressLogin(response.email));
    yield put(setPhoneNumberLogin(response.phoneNumber));
    yield put(setIsPolicyCancelled(response.isPolicyCancelled));
    yield put(setRequireVerificationCode(false));
    const reportDateValidationStatus = yield call(request, reportDateValidateStatusRequest, reportDateValidateStatusRequestOption);
    yield put(setFnolReportDateValidation(reportDateValidationStatus.fnolReportDateValidation));
    // set token expired time
    try {
      localStorage.setItem('expiredTokenTime', (new Date().getTime() + (response.expiresIn * 1000)).toString());
    } catch (err) {
      // Handle the exception gracefully
      Raven.captureException(err);
      // Provide an alternative solution for the user to continue
      // For example, you can display a message to inform the user about the issue and provide a workaround
      // You can also store the token expiration time in memory or use a different storage mechanism if available
    }
    yield put(loginSuccess());
  } catch (err) {
    Raven.captureException(err);
    const locale = yield select(makeSelectLocale());
    const formattedMessage = formatDescriptor(messages.verificationCodeFail, locale);
    yield put(setError(formattedMessage));
    // yield put(showErrorToastr(messages.verificationCodeFail.defaultMessage));
  } finally {
    yield put(endProgress());
    yield put(loading(false));
  }
}

export function isQuote() {
  return isActivePolicy;
}

export function validInceptionDate() {
  return isDateValid;
}

export function resetCustomValidators() {
  isActivePolicy = true;
  isDateValid = true;
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* authenticate() {
  yield takeLatest(VALIDATE_POLICY_INFO, validateEmailPolicyNumber);
  yield takeLatest(VALIDATE_VERIFICATION_CODE, validateVerificationCode);
}



// WEBPACK FOOTER //
// ./app/containers/Login/saga.js